import { useQuery } from 'react-query';
import { getInvoiceDataUseCases } from '@subscriptions/composition';
import type { CsfDataViewDto } from '@subscriptions/application/dtos';

const CSF_EXTRACT_QUERY_KEY = ['subscriptions', 'csf-extract'];

/** Name, size and modification date: the name alone repeats across taxpayers ("constancia.pdf") */
const buildQueryKey = (file: File | null) =>
  file ? [...CSF_EXTRACT_QUERY_KEY, file.name, file.size, file.lastModified] : CSF_EXTRACT_QUERY_KEY;

/**
 * Hook for reading the fiscal data out of an uploaded CSF pdf.
 *
 * Nothing is persisted — the result only prefills the form the user then confirms. Each
 * document's fields are cached for the session, so re-uploading the same file skips the
 * request instead of paying the extraction again.
 *
 * Uses `useQuery` with `enabled: !!file` so React Query handles caching implicitly. Pass
 * `null` to disable the query; pass a file to trigger extraction. Call `refetch()` to
 * retry after an error.
 *
 * @param file - The CSF PDF file to extract, or null to disable the query
 * @returns React Query result with extracted data, loading state, error, and refetch
 */
export const useExtractCsfData = (file: File | null) => {
  const { extractCsfData } = getInvoiceDataUseCases();

  return useQuery<CsfDataViewDto, Error>(buildQueryKey(file), () => extractCsfData.execute(file!), {
    enabled: !!file,
    cacheTime: Infinity,
    staleTime: Infinity,
    retry: false,
  });
};
